import { useMemo } from 'react';
import { usePageDataStore } from '@/features/product-editor/pageStore';
import { BUTTON_LAYOUTS, type ButtonLayout } from '@/types/pageSchema';
import { StanInput } from '@/components';
import { useFormStoreSync } from '@/hooks/useFormStoreSync';
import { useValidator } from '@/features/product-editor/validations/ValidationProvider';

const OPTIONS: { [key in 'TITLE' | 'SUBTITLE' | 'BUTTON']: ButtonLayout[] } = {
    TITLE: [BUTTON_LAYOUTS.BUTTON, BUTTON_LAYOUTS.CALLOUT, BUTTON_LAYOUTS.PREVIEW],
    SUBTITLE: [BUTTON_LAYOUTS.CALLOUT, BUTTON_LAYOUTS.PREVIEW],
    BUTTON: [BUTTON_LAYOUTS.CALLOUT, BUTTON_LAYOUTS.PREVIEW],
};

export default function StoreCardDetails() {
    const storePresentation = usePageDataStore(state => state.pageData?.storePresentation);
    const updatePageData = usePageDataStore(state => state.updatePageData);

    const { storeSectionValidation } = useValidator();
    const {
        register,
        formState: { errors },
    } = storeSectionValidation;

    const setProductTitle = (value: string) => {
        updatePageData({ storePresentation: { title: value } });
    };

    const setProductTagline = (value: string) => {
        updatePageData({ storePresentation: { tagline: value } });
    };

    const setProductButtonText = (value: string) => {
        updatePageData({ storePresentation: { text: value } });
    };

    // Call all hooks unconditionally at the top level
    const titleProps = useFormStoreSync(register('title'), setProductTitle);
    const taglineProps = useFormStoreSync(register('tagline'), setProductTagline);
    const textProps = useFormStoreSync(register('text'), setProductButtonText);

    const optionsToShow = useMemo(() => {
        return Object.keys(OPTIONS).filter(key => {
            return OPTIONS[key as keyof typeof OPTIONS]?.includes(storePresentation.layout);
        });
    }, [storePresentation.layout]);

    return (
        <div className='flex flex-col gap-2'>
            {optionsToShow.includes('TITLE') && (
                <div>
                    <StanInput
                        value={storePresentation.title}
                        {...titleProps}
                        error={!!errors.title}
                        hintText={errors.title?.message}
                    />
                </div>
            )}
            {optionsToShow.includes('SUBTITLE') && (
                <div>
                    <StanInput
                        value={storePresentation.tagline}
                        {...taglineProps}
                        error={!!errors.tagline}
                        hintText={errors.tagline?.message}
                    />
                </div>
            )}
            {optionsToShow.includes('BUTTON') && (
                <div>
                    <StanInput
                        value={storePresentation.text}
                        {...textProps}
                        error={!!errors.text}
                        hintText={errors.text?.message}
                    />
                </div>
            )}
        </div>
    );
}
